import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  ProductInterface,
  ProductPayloadInterface,
  ProductQueryParamsInterface,
  ProductSearchResponseInterface
} from '../interfaces';
import { ResponseApiInterface } from '../../../core/interfaces';

type ProductHttpParamValueType =
  | string
  | number
  | boolean
  | readonly (string | number | boolean)[];

/**
 * Low-level API service for Product HTTP operations.
 *
 * @remarks
 * This service handles raw HTTP calls. Components should NOT use this directly.
 * Use ProductFacade instead for orchestrated operations with messaging.
 *
 * @see docs/modules/product.md for API contract
 */
@Injectable({ providedIn: 'root' })
export class ProductApiService {
  private readonly http = inject(HttpClient);
  private readonly baseURL = `${environment.url}/${environment.prefix}`;
  private readonly endpoint = `${this.baseURL}/products`;

  /**
   * Creates a new product.
   *
   * @param payload - Product data (name, product_code, base_price, cost_price required)
   * @returns Observable with created product response
   */
  create(payload: ProductPayloadInterface): Observable<ResponseApiInterface<ProductInterface>> {
    return this.http.post<ResponseApiInterface<ProductInterface>>(this.endpoint, payload);
  }

  /**
   * Updates an existing product.
   *
   * @param productId - Product UUID
   * @param payload - Updated data (product_code cannot be changed)
   * @returns Observable with updated product response
   */
  update(
    productId: string,
    payload: Partial<ProductPayloadInterface>
  ): Observable<ResponseApiInterface<ProductInterface>> {
    const url = `${this.endpoint}/${productId}`;
    return this.http.put<ResponseApiInterface<ProductInterface>>(url, payload);
  }

  /**
   * Gets product details by ID.
   *
   * @param productId - Product UUID
   * @returns Observable with product response
   */
  getById(productId: string): Observable<ResponseApiInterface<ProductInterface>> {
    const url = `${this.endpoint}/${productId}`;
    return this.http.get<ResponseApiInterface<ProductInterface>>(url);
  }

  /**
   * Lists products with optional filters.
   *
   * @param params - Query parameters for filtering/pagination
   * @returns Observable with products array response
   */
  getAll(
    params: ProductQueryParamsInterface = {}
  ): Observable<ResponseApiInterface<ProductInterface[]>> {
    return this.http.get<ResponseApiInterface<ProductInterface[]>>(this.endpoint, {
      params: this.toHttpParams(params)
    });
  }

  /**
   * Soft deletes a product.
   *
   * @param productId - Product UUID
   * @returns Observable<void>
   */
  delete(productId: string): Observable<void> {
    const url = `${this.endpoint}/${productId}`;
    return this.http.delete<void>(url);
  }

  /**
   * Searches products for autocomplete/select.
   *
   * @param query - Search query (min 2 chars)
   * @returns Observable with options array
   */
  search(query: string): Observable<ProductSearchResponseInterface[]> {
    const url = `${this.endpoint}/search`;
    return this.http.get<ProductSearchResponseInterface[]>(url, {
      params: { q: query }
    });
  }

  private toHttpParams(
    params: ProductQueryParamsInterface
  ): Record<string, ProductHttpParamValueType> {
    const httpParams: Record<string, ProductHttpParamValueType> = {};

    Object.keys(params).forEach((key) => {
      const value = params[key];

      if (value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      httpParams[key] = value;
    });

    return httpParams;
  }
}
